import { catalog, getItem, QUALITIES, type Quality } from '@wyn/content';
import type { BookResponse } from '@wyn/contracts';
import { bookTopic } from '@wyn/game-core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { GameShell } from '../components/Layout.tsx';
import { ErrorNotice } from '../components/Notice.tsx';
import { Callout, PageTitle } from '../components/ui.tsx';
import { Icon } from '../components/Icon.tsx';
import { UI } from '../icons/ui-icons.ts';
import { bookQuery, ordersQuery, useAction } from '../lib/queries.ts';
import { useFollow } from '../lib/realtime.ts';
import { coin } from '../lib/time.ts';
import { useMe } from '../lib/useMe.ts';
import { Loading } from './Landing.tsx';

const ITEMS = [...catalog.items].sort((a, b) => a.name.localeCompare(b.name));
const REFETCH = ['book', 'orders', 'me'] as const;

/** The realm-wide order book: bid for goods or offer your own, one good and quality at a time. */
export function Market() {
  const me = useMe();
  const firstHeld = me.data?.inventory[0];
  const [itemKey, setItemKey] = useState<string | null>(null);
  const [quality, setQuality] = useState<Quality | null>(null);
  if (me.isPending) return <Loading />;
  if (!me.data?.player) return <Navigate to="/" />;
  const item = itemKey ?? firstHeld?.itemKey ?? 'arrow';
  const q = quality ?? firstHeld?.quality ?? 'common';
  const pick = (k: string, ql: Quality) => {
    setItemKey(k);
    setQuality(ql);
  };
  return (
    <GameShell me={me.data} skew={me.skew}>
      <div className="space-y-6">
        <section>
          <PageTitle icon={UI.market}>The Market</PageTitle>
          <p className="text-ink-dim">
            One book for the whole realm. What you offer is held in escrow until it sells or you withdraw it. Sellers pay a 5% fee (Merchants 4%).
          </p>
        </section>
        <section className="panel flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2">
            <span className="smallcaps text-sm text-ink-dim">Good</span>
            <select className="field" aria-label="Good" value={item} onChange={(e) => setItemKey(e.target.value)}>
              {ITEMS.map((i) => <option key={i.key} value={i.key}>{i.name}</option>)}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <span className="smallcaps text-sm text-ink-dim">Quality</span>
            <select className="field" aria-label="Quality" value={q} onChange={(e) => setQuality(e.target.value as Quality)}>
              {QUALITIES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
        </section>
        <div className="grid gap-6 lg:grid-cols-2">
          <Book itemKey={item} quality={q} />
          <OrderForm key={`${item}#${q}`} itemKey={item} quality={q} />
        </div>
        <YourPack onPick={pick} />
        <YourOrders />
      </div>
    </GameShell>
  );
}

function Levels({ title, rows, tone }: { title: string; rows: BookResponse['buys']; tone: string }) {
  return (
    <div>
      <h3 className={`smallcaps mb-1 text-sm ${tone}`}>{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-ink-faint">None.</p>
      ) : (
        <table className="w-full font-mono text-sm tabular-nums">
          <tbody>
            {rows.slice(0, 8).map((r) => (
              <tr key={r.price}>
                <td>{coin(r.price)}</td>
                <td className="text-right">×{r.quantity}</td>
                <td className="text-right text-ink-faint">{r.orders} order{r.orders === 1 ? '' : 's'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Book({ itemKey, quality }: { itemKey: string; quality: Quality }) {
  const book = useQuery(bookQuery(itemKey, quality));
  // Changes to this book are pushed while the page is open.
  useFollow('market-book', [bookTopic(itemKey, quality)]);
  return (
    <section className="panel space-y-3" aria-label="Order book">
      <h2 className="heading"><Icon name={getItem(itemKey).icon} className="mr-1" />{getItem(itemKey).name} <span className="text-ink-faint">· {quality}</span></h2>
      <div className="grid grid-cols-2 gap-4">
        <Levels title="Offered (asks)" rows={book.data?.sells ?? []} tone="text-blood" />
        <Levels title="Wanted (bids)" rows={book.data?.buys ?? []} tone="text-verdigris" />
      </div>
      <ErrorNotice error={book.error} />
    </section>
  );
}

function OrderForm({ itemKey, quality }: { itemKey: string; quality: Quality }) {
  const me = useMe();
  const place = useAction('placeOrder', { refetch: [...REFETCH] });
  const qc = useQueryClient();
  // Matching runs behind the reply (a queue, one book at a time), so look again once it has had a moment.
  const lookAgain = () => {
    for (const ms of [800, 2500]) setTimeout(() => REFETCH.forEach((key) => void qc.invalidateQueries({ queryKey: [key] })), ms);
  };
  const held = me.data!.inventory.find((i) => i.itemKey === itemKey && i.quality === quality)?.quantity ?? 0;
  const [side, setSide] = useState<'buy' | 'sell'>(held > 0 ? 'sell' : 'buy');
  const [price, setPrice] = useState(Math.max(1, getItem(itemKey).value ?? 1));
  const [quantity, setQuantity] = useState(1);
  const purse = me.data!.player!.purse;
  const short = side === 'sell' ? quantity > held : price * quantity > purse;
  const last = place.data?.outcome;
  return (
    <section className="panel space-y-3">
      <h2 className="heading">Place an order</h2>
      <div className="flex gap-2" role="radiogroup" aria-label="Side">
        {(['sell', 'buy'] as const).map((s) => (
          <button key={s} type="button" role="radio" aria-checked={side === s} onClick={() => setSide(s)} className={`border px-3 py-1 ${side === s ? 'border-gilt text-ink' : 'border-rule text-ink-dim'}`}>
            {s === 'sell' ? 'Sell' : 'Buy'}
          </button>
        ))}
      </div>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          place.mutate({ side, itemKey, quality, price, quantity }, { onSuccess: lookAgain });
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-dim">Price each</span>
          <input className="field w-28 font-mono" type="number" min={1} aria-label="Price each" value={price} onChange={(e) => setPrice(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-ink-dim">Quantity{side === 'sell' && ` (you have ${held})`}</span>
          <input className="field w-24 font-mono" type="number" min={1} aria-label="Quantity" value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
        </label>
        <button className="btn" type="submit" disabled={place.isPending || short}>
          {side === 'sell' ? 'Offer for sale' : `Bid · ${coin(price * quantity)}`}
        </button>
      </form>
      {short && <p className="text-sm text-ink-faint">{side === 'sell' ? 'You do not have that many.' : 'Your purse is too light.'}</p>}
      {last && !place.isPending && (
        <Callout tone="good" role="status">
          Your {last.side === 'sell' ? 'offer' : 'bid'} of {last.quantity} at {coin(last.price)} is on the book. Anything that crosses fills straight away.
        </Callout>
      )}
      <ErrorNotice error={place.error} />
    </section>
  );
}

function YourPack({ onPick }: { onPick: (itemKey: string, quality: Quality) => void }) {
  const inv = useMe().data!.inventory;
  if (inv.length === 0) return null;
  return (
    <section className="panel space-y-2">
      <h2 className="heading">Your pack</h2>
      <ul className="flex flex-wrap gap-2">
        {inv.map((i) => (
          <li key={`${i.itemKey}#${i.quality}`}>
            <button type="button" className="btn-quiet text-sm" onClick={() => onPick(i.itemKey, i.quality)}>
              <Icon name={getItem(i.itemKey).icon} className="mr-1" />{getItem(i.itemKey).name} <span className="text-ink-faint">{i.quality} ×{i.quantity}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function YourOrders() {
  const orders = useQuery(ordersQuery());
  const cancel = useAction('cancelOrder', { refetch: [...REFETCH] });
  const list = orders.data?.orders ?? [];
  return (
    <section className="panel space-y-2">
      <h2 className="heading">Your orders</h2>
      {list.length === 0 ? (
        <p className="text-sm text-ink-faint">Nothing on the book.</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {list.map((o) => (
            <li key={o.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-rule/60 pb-1 last:border-0">
              <span>
                <span className={o.side === 'sell' ? 'text-blood' : 'text-verdigris'}>{o.side === 'sell' ? 'Selling' : 'Buying'}</span> {o.remaining} of {o.quantity} × {o.quality}{' '}
                {getItem(o.itemKey).name.toLowerCase()} at {coin(o.price)}
              </span>
              <button type="button" className="btn-quiet py-0 text-xs" disabled={cancel.isPending} onClick={() => cancel.mutate({ orderId: o.id })}>
                withdraw
              </button>
            </li>
          ))}
        </ul>
      )}
      <ErrorNotice error={cancel.error ?? orders.error} />
    </section>
  );
}
